import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import PageLayout from '../../../components/feature/PageLayout';
import { getMinistryById, getAllMinistries } from '../../../data/ministries';

const ServiceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ministry = id ? getMinistryById(id) : null;
  const allMinistries = getAllMinistries();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!ministry) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold text-burgundy-900 mb-4">Ministry Not Found</h1>
          <Link to="/services" className="text-burgundy-700 hover:text-gold-600 cursor-pointer whitespace-nowrap">
            ← Back to Services
          </Link>
        </div>
      </PageLayout>
    );
  }

  const currentIndex = allMinistries.findIndex(m => m.id === id);
  const previousMinistry = currentIndex > 0 ? allMinistries[currentIndex - 1] : null;
  const nextMinistry = currentIndex < allMinistries.length - 1 ? allMinistries[currentIndex + 1] : null;

  return (
    <PageLayout>
      {/* Breadcrumb Navigation */}
      <div className="bg-burgundy-50 border-b border-burgundy-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-burgundy-600 hover:text-burgundy-800 cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-burgundy-400"></i>
            <Link to="/services" className="text-burgundy-600 hover:text-burgundy-800 cursor-pointer whitespace-nowrap">
              Services
            </Link>
            <i className="ri-arrow-right-s-line text-burgundy-400"></i>
            <span className="text-burgundy-900 font-medium">{ministry.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img
          src={ministry.heroImage || ministry.image}
          alt={ministry.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl px-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 sm:mb-6 mx-auto">
              <i className={`${ministry.icon} text-3xl sm:text-4xl md:text-5xl text-white`}></i>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">{ministry.title}</h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90">{ministry.subtitle || ministry.description}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Back Button */}
        <Link
          to="/services"
          className="inline-flex items-center text-burgundy-700 hover:text-gold-600 font-medium mb-8 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back to All Services
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Ministry Image */}
            <div className="w-full h-56 sm:h-72 md:h-96 rounded-lg overflow-hidden shadow-lg">
              <img
                src={ministry.image}
                alt={ministry.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-burgundy-900 mb-4">About This Ministry</h2>
              <p className="text-gray-700 leading-relaxed text-base mb-4">
                {ministry.description}
              </p>
              {ministry.extendedDescription && (
                <p className="text-gray-700 leading-relaxed text-base">
                  {ministry.extendedDescription}
                </p>
              )}
            </div>

            {/* Key Features */}
            <div>
              <h2 className="text-2xl font-bold text-burgundy-900 mb-4">What We Offer</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ministry.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200"
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-gold-600 text-xl"></i>
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Photo Gallery */}
            {ministry.gallery && ministry.gallery.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-burgundy-900 mb-4">Photo Gallery</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {ministry.gallery.map((photo, index) => (
                    <div key={index} className="w-full h-32 sm:h-40 md:h-48 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                      <img
                        src={photo}
                        alt={`${ministry.title} gallery ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Testimonials */}
            {ministry.testimonials && ministry.testimonials.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-burgundy-900 mb-4">What People Say</h2>
                <div className="space-y-4">
                  {ministry.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gradient-to-br from-burgundy-50 to-gold-50 p-6 rounded-lg border border-burgundy-100">
                      <div className="flex items-start space-x-1 mb-3">
                        <i className="ri-double-quotes-l text-gold-600 text-2xl"></i>
                      </div>
                      <p className="text-gray-700 italic mb-4 text-sm leading-relaxed">
                        {testimonial.quote}
                      </p>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-burgundy-700 rounded-full text-white font-bold text-sm">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-burgundy-900 text-sm">{testimonial.name}</p>
                          <p className="text-xs text-gray-600">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leadership/Contact */}
            {ministry.leadership && (
              <div>
                <h2 className="text-2xl font-bold text-burgundy-900 mb-4">Ministry Leadership</h2>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-burgundy-700 rounded-full text-white font-bold text-xl flex-shrink-0">
                      {ministry.leadership.name.split(' ').map(n => n.charAt(0)).join('')}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-burgundy-900 text-lg">{ministry.leadership.name}</h3>
                      <p className="text-gold-600 font-medium text-sm mb-2">{ministry.leadership.title}</p>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">{ministry.leadership.bio}</p>
                      {ministry.leadership.contact && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <i className="ri-mail-line text-burgundy-700"></i>
                          <a href={`mailto:${ministry.leadership.contact}`} className="hover:text-burgundy-700 cursor-pointer">
                            {ministry.leadership.contact}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Details */}
            {ministry.scheduleDetails && (
              <div>
                <h2 className="text-2xl font-bold text-burgundy-900 mb-4">Detailed Schedule</h2>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {ministry.scheduleDetails.map((schedule, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-burgundy-900 text-sm mb-1">{schedule.day}</h4>
                            <p className="text-gray-600 text-sm">{schedule.activity}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="font-medium text-burgundy-700 text-sm">{schedule.time}</p>
                            <p className="text-xs text-gray-500">{schedule.location}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-burgundy-50 to-gold-50 rounded-lg p-6 lg:sticky lg:top-24 space-y-6">
              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center bg-burgundy-700 rounded-full mx-auto">
                <i className={`${ministry.icon} text-3xl text-white`}></i>
              </div>

              {/* Schedule Info */}
              <div className="text-center border-b border-burgundy-200 pb-6">
                <h3 className="text-lg font-bold text-burgundy-900 mb-2">Schedule</h3>
                <p className="text-burgundy-700 font-medium text-sm">{ministry.schedule}</p>
              </div>

              {/* Location Info */}
              <div className="text-center border-b border-burgundy-200 pb-6">
                <h3 className="text-lg font-bold text-burgundy-900 mb-2">Location</h3>
                <p className="text-burgundy-700 font-medium text-sm">{ministry.location}</p>
              </div>

              {/* Quick Info */}
              {ministry.quickInfo && (
                <div className="text-center border-b border-burgundy-200 pb-6">
                  <h3 className="text-lg font-bold text-burgundy-900 mb-3">Quick Info</h3>
                  <div className="space-y-2 text-sm">
                    {ministry.quickInfo.map((info, index) => (
                      <div key={index} className="flex items-center justify-center space-x-2 text-gray-700">
                        <i className="ri-checkbox-circle-line text-gold-600"></i>
                        <span>{info}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Get Involved CTA */}
              <div className="text-center pt-2">
                <h3 className="text-lg font-bold text-burgundy-900 mb-3">Get Involved</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Interested in joining this ministry? Contact us to learn more.
                </p>
                <Link
                  to="/contact"
                  className="inline-block bg-burgundy-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-burgundy-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Previous/Next Ministry Navigation */}
      <div className="bg-burgundy-50 border-t border-burgundy-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center justify-between w-full sm:w-auto gap-4">
            {previousMinistry ? (
              <Link
                to={`/services/${previousMinistry.id}`}
                className="flex items-center space-x-2 sm:space-x-3 text-burgundy-700 hover:text-burgundy-900 transition-colors group cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl sm:text-2xl group-hover:-translate-x-1 transition-transform"></i>
                <div className="text-left">
                  <div className="text-xs text-burgundy-600 mb-1">Previous Ministry</div>
                  <div className="font-semibold text-sm sm:text-base">{previousMinistry.title}</div>
                </div>
              </Link>
            ) : (
              <div></div>
            )}

            <Link
              to="/services"
              className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-burgundy-700 rounded-lg font-semibold hover:bg-burgundy-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              All Services
            </Link>

            {nextMinistry ? (
              <Link
                to={`/services/${nextMinistry.id}`}
                className="flex items-center space-x-2 sm:space-x-3 text-burgundy-700 hover:text-burgundy-900 transition-colors group cursor-pointer"
              >
                <div className="text-right">
                  <div className="text-xs text-burgundy-600 mb-1">Next Ministry</div>
                  <div className="font-semibold text-sm sm:text-base">{nextMinistry.title}</div>
                </div>
                <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
            ) : (
              <div></div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Ministries Section */}
      <div className="bg-gray-50 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-burgundy-900 mb-3">Explore Other Ministries</h2>
            <p className="text-gray-600 text-base">
              Discover more ways to serve and grow in faith
            </p>
          </div>
          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-burgundy-700 text-white px-8 py-3 rounded-lg font-medium hover:bg-burgundy-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Services
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ServiceDetailPage;
